import reportingV2Headers from '@/Reporting/config/table/reporting-v2-headers';
import reportingV2DateRanges from '@/Reporting/config/reporting-v2-date-ranges';
import { PreferenceKeys } from '@/Core/config/preferences';
import {
  startOfYear,
  startOfWeek,
  startOfMonth,
  subDays,
  subMonths,
  subYears,
  format,
} from 'date-fns';
import axios from 'axios';
import { TEAM_MEMBER } from '@/Reporting/config/constants';

const initialState = {
  headers: reportingV2Headers,
  membersData: [],
  ranges: reportingV2DateRanges,
  graphData: [],
};

const DATE_FORMAT = 'ddd, MMM D, YYYY';

export default {
  namespaced: true,
  state: {
    headers: [],
    membersData: [],
    ranges: [],
    error: null,
    isLoading: false,
  },
  mutations: {
    setReportingV2Headers(state, headersList) {
      state.headers = [...headersList];
    },
    setReportingV2Data(state, dataList) {
      state.membersData = [...dataList];
    },
    setReportingV2Error(state, error) {
      state.error = error;
    },
    setReportingV2Loading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setReportingV2DateRanges(state, rangesList) {
      state.ranges = [...rangesList];
    },
    setReportingV2DateRangesHints(state) {
      // The section below calculates the dates exclusively
      // for use as strings in the Date Ranges Hints captions.
      // The actual calculation to match with the menu options
      //  and display the data should come from the backend.
      const today = new Date();
      const currentDay = format(today, DATE_FORMAT);
      const yesterday = format(subDays(today, 1), DATE_FORMAT);
      const dayBeforeYesterday = format(subDays(today, 2), DATE_FORMAT);

      const monday = format(startOfWeek(today, { weekStartsOn: 1 }), DATE_FORMAT);

      let thisWeek;
      if (monday === currentDay) {
        thisWeek = currentDay;
      } else {
        thisWeek = `${monday} - ${currentDay}`;
      }

      const mondayOfLastWeek = format(
        subDays(startOfWeek(today, { weekStartsOn: 1 }), 7),
        DATE_FORMAT,
      );
      const sundayOfLastWeek = format(
        subDays(startOfWeek(today, { weekStartsOn: 1 }), 1),
        DATE_FORMAT,
      );
      const lastWeek = `${mondayOfLastWeek} - ${sundayOfLastWeek}`;

      const mondayOfWeekBeforeLastWeek = format(
        subDays(startOfWeek(today, { weekStartsOn: 1 }), 14),
        DATE_FORMAT,
      );
      const sundayOfWeekBeforeLastWeek = format(subDays(mondayOfLastWeek, 1), DATE_FORMAT);
      const compareToLastWeek = `${mondayOfWeekBeforeLastWeek} - ${sundayOfWeekBeforeLastWeek}`;

      const firstDayOfThisMonth = format(startOfMonth(today), DATE_FORMAT);
      let thisMonth;
      if (firstDayOfThisMonth === currentDay) {
        thisMonth = `${currentDay}`;
      } else {
        thisMonth = `${firstDayOfThisMonth} - ${currentDay}`;
      }

      const firstDayOfLastMonth = format(subMonths(startOfMonth(today), 1), DATE_FORMAT);
      const lastDayOfLastMonth = format(subDays(startOfMonth(today), 1), DATE_FORMAT);
      const lastMonth = `${firstDayOfLastMonth} - ${lastDayOfLastMonth}`;

      const compareToLastMonthStartDate = format(subMonths(startOfMonth(today), 2), DATE_FORMAT);
      const compareToLastMonthEndDate = format(subDays(firstDayOfLastMonth, 1), DATE_FORMAT);
      const compareToLastMonth = `${compareToLastMonthStartDate} - ${compareToLastMonthEndDate}`;

      const firstDayOfThisYear = format(startOfYear(today), DATE_FORMAT);
      let thisYear;
      if (firstDayOfThisYear === currentDay) {
        thisYear = `${currentDay}`;
      } else {
        thisYear = `${firstDayOfThisYear} - ${currentDay}`;
      }

      const firstDayOfLastYear = format(subYears(startOfYear(today), 1), DATE_FORMAT);
      const lastDayOfLastYear = format(subDays(startOfYear(today), 1), DATE_FORMAT);
      const lastYear = `${firstDayOfLastYear} - ${lastDayOfLastYear}`;

      const compareToLastYearStartDate = format(subYears(startOfYear(today), 2), DATE_FORMAT);
      const compareToLastYearEndDate = format(subDays(firstDayOfLastYear, 1), DATE_FORMAT);
      const compareToLastYear = `${compareToLastYearStartDate} - ${compareToLastYearEndDate}`;

      const sevenDaysAgo = format(subDays(today, 6), DATE_FORMAT);
      const last7Days = `${sevenDaysAgo} - ${currentDay}`;

      const fourteenDaysAgo = format(subDays(today, 13), DATE_FORMAT);
      const last14Days = `${fourteenDaysAgo} - ${currentDay}`;

      const thirtyDaysAgo = format(subDays(today, 29), DATE_FORMAT);
      const last30Days = `${thirtyDaysAgo} - ${currentDay}`;

      const ninetyDaysAgo = format(subDays(today, 89), DATE_FORMAT);
      const last90Days = `${ninetyDaysAgo} - ${currentDay}`;

      const compareToLast7DaysEndDate = format(subDays(today, 7), DATE_FORMAT);
      const compareToLast7Days = `${fourteenDaysAgo} - ${compareToLast7DaysEndDate}`;

      const compareToLast14DaysStartDate = format(subDays(today, 27), DATE_FORMAT);
      const compareToLast14DaysEndDate = format(subDays(fourteenDaysAgo, 1), DATE_FORMAT);
      const compareToLast14Days = `${compareToLast14DaysStartDate} - ${compareToLast14DaysEndDate}`;

      const comparetoLast30DaysStartDate = format(subDays(today, 59), DATE_FORMAT);
      const comparetoLast30DaysEndDate = format(subDays(thirtyDaysAgo, 1), DATE_FORMAT);
      const comparetoLast30Days = `${comparetoLast30DaysStartDate} - ${comparetoLast30DaysEndDate}`;

      const comparetoLast90DaysStartDate = format(subDays(today, 179), DATE_FORMAT);
      const comparetoLast90DaysEndDate = format(subDays(ninetyDaysAgo, 1), DATE_FORMAT);
      const comparetoLast90Days = `${comparetoLast90DaysStartDate} - ${comparetoLast90DaysEndDate}`;

      state.ranges[0].hint = `(${currentDay})`;
      state.ranges[1].hint = `(${yesterday})`;
      state.ranges[2].hint = `(${thisWeek})`;
      state.ranges[3].hint = `(${last7Days})`;
      state.ranges[4].hint = `(${lastWeek})`;
      state.ranges[5].hint = `(${last14Days})`;
      state.ranges[6].hint = `(${thisMonth})`;
      state.ranges[7].hint = `(${last30Days})`;
      state.ranges[8].hint = `(${lastMonth})`;
      state.ranges[9].hint = `(${last90Days})`;
      state.ranges[10].hint = `(${thisYear})`;
      state.ranges[11].hint = `(${lastYear})`;

      state.ranges[0].compared_period = `(${yesterday})`;
      state.ranges[1].compared_period = `(${dayBeforeYesterday})`;
      state.ranges[2].compared_period = `(${lastWeek})`;
      state.ranges[3].compared_period = `(${compareToLast7Days})`;
      state.ranges[4].compared_period = `(${compareToLastWeek})`;
      state.ranges[5].compared_period = `(${compareToLast14Days})`;
      state.ranges[6].compared_period = `(${lastMonth})`;
      state.ranges[7].compared_period = `(${comparetoLast30Days})`;
      state.ranges[8].compared_period = `(${compareToLastMonth})`;
      state.ranges[9].compared_period = `(${comparetoLast90Days})`;
      state.ranges[10].compared_period = `(${lastYear})`;
      state.ranges[11].compared_period = `(${compareToLastYear})`;
    },
    updateHeaders(state, headers) {
      state.headers = headers;
    },
  },
  actions: {
    fetchReportingV2Headers({ commit }) {
      const savedHeaders = localStorage.getItem(PreferenceKeys.ReportingV2Headers);

      if (savedHeaders) {
        commit('setReportingV2Headers', JSON.parse(savedHeaders));
        return;
      }

      const headersList = initialState.headers;
      commit('setReportingV2Headers', headersList);
    },
    fetchReportingV2DateRanges({ commit }) {
      const rangesList = initialState.ranges;
      commit('setReportingV2DateRanges', rangesList);
    },
    fetchReportingV2DateRangesHints({ commit }) {
      commit('setReportingV2DateRangesHints');
    },
    updateHeaders({ commit }, newHeaders) {
      const [nameHeader] = reportingV2Headers;
      const headers = newHeaders.map(header => Object.assign({}, header));
      // We don't pass the name to the customize modal and, because of that,
      // we need to backfill it when saving the current headers to localhost.
      const finalHeaders = [nameHeader, ...headers];

      localStorage.setItem(PreferenceKeys.ReportingV2Headers, JSON.stringify(finalHeaders));
      commit('updateHeaders', finalHeaders);
    },
    async getInfoForDataTable({ commit }, { selectedHeaders, dateRange, selectedFilter }) {
      commit('setReportingV2Loading', true);

      const eventTypes = selectedHeaders
        .filter(header => header.visible && header.value !== TEAM_MEMBER)
        .map(header => header.value);

      let request = {
        event_types: eventTypes,
        date_range: dateRange,
        target: '',
        target_agent_type: '',
        target_ids: [],
      };

      if (selectedFilter && selectedFilter.length) {
        request.target = selectedFilter[0].target;
        request.target_ids = selectedFilter.map(i => i.id);

        if (selectedFilter[0].target === 'agent') {
          request.target_agent_type = selectedFilter[0].target_type;
        }
      }

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_EVENTS_API_BASE_URL}/reporting/table-data`,
          request,
        );
        commit('setReportingV2Data', res.data.reports);
      } catch (error) {
        commit('setReportingV2Error', error);
      } finally {
        commit('setReportingV2Loading', false);
      }
    },
  },
};
